import React, {useLayoutEffect} from "react";
import _ from "lodash";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5locales_it_IT from "@amcharts/amcharts5/locales/it_IT"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {getTooltip, getXYChart, addCursor, getRenderAxisX, getRenderAxisY, COLORS} from "./commons";


const getChartHigh = (n) => {
  switch (true) {
    case (n < 2):
      return 'h-24'
    case (n < 4):
      return 'h-48'
    case (n < 6):
      return 'h-72'
    case (n < 8):
      return 'h-96'
    case (n < 11):
      return 'h-128'
    default:
      return 'h-152'
  }
}

function KpiConfigurationBarChart({id, data, intl, price}) {

  const amount = intl.formatMessage({id: 'total_amount_kk'})
  const configurations = intl.formatMessage({id: 'configurations'})

  useLayoutEffect(() => {

    let root = am5.Root.new(id);
    root.setThemes([am5themes_Animated.new(root)])
    root.locale = am5locales_it_IT;
    root.numberFormatter.set("numberFormat", "#,###.");

    // Create chart
    let chart = getXYChart(root, {layout: root.verticalLayout})
    addCursor(root, chart)

    let legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
      })
    );

    // Create axes
    let yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "name",
      renderer: getRenderAxisY(root, false, true)
    }))

    let yRenderer = yAxis.get("renderer");
    yRenderer.labels.template.setAll({
      fontSize: 11,
      fontWeight: "bold",
      paddingRight: 20,
      oversizedBehavior: "truncate",
      maxWidth: 180,
    });
    yAxis.data.setAll(data);

    let xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
      calculateTotals: true,
      renderer: getRenderAxisX(root, false),
      extraMax: 0.2,
      min: 0
    }))
    xAxis.get("renderer").labels.template.set("forceHidden", true);

    // Add series
    function createSeries(field, name, color, showTotal) {
      let series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: field,
        valueYField: price ? 'val_configurations' : 'amt_completed',
        stacked: true,
        categoryYField: "name",
        fill: color,
        maskBullets: false,
        tooltip: getTooltip(root, price
          ? `[bold]{categoryY}[/]\n${amount}: {valueX}€ \n${configurations}: {valueY}`
          : `[bold]{categoryY}[/]\n{name}: {valueX}`)
      }))

      series.columns.template.setAll({
        tooltipY: am5.percent(90)
      });

      // tooltip: getTooltip(root, `[bold]{categoryY}[/] \n ${amount}: {valueX}€ \n ${cantieri}: {valueY}`)

      if (!price) {
        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            sprite: am5.Label.new(root, {
              text: "{valueX}",
              fontSize: 9,
              fill: root.interfaceColors.get("alternativeText"),
              centerY: am5.p50,
              centerX: am5.p50,
              populateText: true
            })
          });
        });

        series.columns.template.onPrivate("width", function (width, target) {
          am5.array.each(target.dataItem.bullets, function (bullet) {
            if (width > 20 || bullet.get("sprite").get("text").includes('valueXTotal')) {
              bullet.get("sprite").show();
            } else {
              bullet.get("sprite").hide();
            }
          });
        });

      }
      if (showTotal) {
        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            locationX: 1,
            sprite: am5.Label.new(root, {
              text: price ? "[bold]{valueXTotal}€[/]" : "[bold]{valueXTotal}[/]",
              centerY: am5.p50,
              centerX: am5.p0,
              populateText: true
            })
          });
        });
      }

      legend.data.push(series);
      series.data.setAll(data);
      series.appear(1000);

    }

    if (price) {
      createSeries('amt_completed', intl.formatMessage({id: 'completed'}), COLORS['completed'], true)
    } else {
      createSeries('val_configurations_not_completed', intl.formatMessage({id: 'not_completed'}), COLORS['not_completed'])
      createSeries('val_configurations_completed', intl.formatMessage({id: 'completed'}), COLORS['completed'], true)
    }

    return () => {
      root.dispose()
    }
  }, [data, id, price]) // eslint-disable-line react-hooks/exhaustive-deps

  return <div id={id} className={`w-full ${getChartHigh(data.length)}`}/>
}

export default React.memo(KpiConfigurationBarChart, (props, nextProps) => {
  return _.isEqual(props.data, nextProps.data) && _.isEqual(props.price, nextProps.price)
})
